// client/src/utils/competitorSummary.ts
//
// The Competitor's Total box in the quotation Pricing Summary.
//
// CLIENT-ONLY, and deliberately not part of `utils/pricing.ts` next door: that
// file is MIRRORED against `src/utils/pricing.js` and adding a half to one side
// of a mirror is how the parity harness earns its keep. Nothing on the server
// summarises competitor prices — the exports print the raw per-line
// `competitorPrice` and never total it — so this rule has one owner and it is
// this file.

import { ceilPrice } from './priceUtils';
import { DEFAULT_MARKUP_PERCENT, resolveItemPricing, resolveQuotationTotals, profitPercents, realizedMarkupPercent, PricedLine } from './pricing';

/**
 * A line's competitor price, or 0.
 *
 * Read straight off the ITEM. It used to be mirrored into a separate map keyed
 * by ARRAY INDEX, which silently drifted the moment a line was deleted:
 * handleRemoveItem filters the array, every row below the deletion shifts up
 * one index, and each inherited the competitor price of the row that used to
 * sit beneath it — repricing the Competitor's Total with no visible cause.
 */
export const competitorPriceOf = (
  item: { competitorPrice?: number | null } | null | undefined
): number => Number(item?.competitorPrice) || 0;

/** A line as this module reads it: what prices it, plus what the competitor charges for it. */
export type CompetitorLine = PricedLine & {
  competitorPrice?: number | null;
  quantity?: number | null;
  pricingSource?: string | null;
};

export interface CompetitorSummary {
  totalPrice: number;
  profit: number;
  profitPercentage: number;
  marginPercentage: number;
  finalPrice: number;
  /**
   * The after-discount selling total profit is measured from — null when no
   * competitor price is entered anywhere (profit is then 0 by rule, not
   * "selling 0 less the cost"). Lets the page state the TT$ profit through
   * `resolveProfitTtd`, the same owner Unichand's Total uses.
   */
  netSellingPrice: number | null;
}

export interface CompetitorSummaryInput {
  /** Markup for lines carrying none — the same figure Unichand's Total is built on. */
  defaultMarkupPercent?: number;
  /** Cost of every line, competitor-priced or not. Profit is measured against all of it. */
  totalCostPrice: number;
  discountPercentage: number;
  transportationCharges: number;
  additionalCharges: number;
  customExpensesTotal: number;
}

/**
 * The Competitor's Total box.
 *
 * The box is a WHAT-IF. A line with no competitor price falls back to OUR
 * selling price on purpose, so it reads "what would this quote total if we
 * matched the competitor where we know their price and left the rest alone" —
 * an unpriced line means "no change on this line".
 *
 * That fallback only means something when a competitor price exists SOMEWHERE.
 * Nobody fills the Competitor's Price column on most quotes, and with the column
 * all zeros the fallback fired on every line and made the box a byte-identical
 * copy of Unichand's Total — same Total Price, same Profit, same Margin %. The
 * screen showed a competitor comparison, in competitor-labelled cells, built
 * entirely out of our own numbers, and it read as real because every figure in
 * it WAS real. QUO-00012 / RFQ-CT-0142: $1390.80, $331.41 (31.3%), 23.8%, all
 * borrowed from our own side of the summary.
 *
 * So with no competitor price entered anywhere, the box is zero — which is what
 * "we have no competitor price" actually looks like. The rows underneath it
 * (discount, additional charges, custom expenses) are typed by
 * hand rather than derived, so they still count toward Final Price on a zero
 * total.
 */
export const resolveCompetitorSummary = (
  items: ReadonlyArray<CompetitorLine> | null | undefined,
  {
    defaultMarkupPercent = DEFAULT_MARKUP_PERCENT,
    totalCostPrice,
    discountPercentage,
    transportationCharges,
    additionalCharges,
    customExpensesTotal,
  }: CompetitorSummaryInput
): CompetitorSummary => {
  const lines = items ?? [];
  const anyEntered = lines.some((item) => competitorPriceOf(item) > 0);

  // Restate each line at the competitor's price and hand the result to the same
  // owner the Unichand box uses, so the two sides of the summary are compared
  // under identical rules. Doing the arithmetic here instead is how this box came
  // to have its own profit-before-discount and its own pre-discount margin
  // denominator — the very defects `resolveQuotationTotals` was written to end.
  const restated = lines.map((item) => {
    const competitorPrice = competitorPriceOf(item);
    return {
      totalPrice: Number(item?.totalPrice) || 0,
      totalSellingPrice:
        competitorPrice > 0
          ? ceilPrice(competitorPrice * (Number(item?.quantity) || 0))
          : resolveItemPricing(item, defaultMarkupPercent).totalSellingPrice,
    };
  });

  const totals = resolveQuotationTotals(anyEntered ? restated : [], {
    defaultMarkupPercent,
    discountPercent: discountPercentage,
    transportationCharges,
    additionalCharges,
    customExpensesTotal,
  });

  // The cost side comes from the caller, not from `restated`: profit here is what
  // USC would earn AT the competitor's price, so it is measured against the cost
  // of every line — including the ones the competitor never priced.
  const profit = anyEntered ? totals.netSellingPrice - totalCostPrice : 0;

  const { profitPercentOfCost, marginPercent } = profitPercents(profit, totalCostPrice, totals.netSellingPrice);

  return {
    totalPrice: totals.sellingBeforeDiscount,
    profit,
    profitPercentage: profitPercentOfCost,
    marginPercentage: marginPercent,
    finalPrice: totals.finalPrice,
    netSellingPrice: anyEntered ? totals.netSellingPrice : null,
  };
};

/**
 * Competitor markup on COST, per line (owner decision D97, 2026-10-06): how
 * much the competitor's price (IFS's fixed quarterly list, or whatever was
 * typed) marks up OUR CURRENT cost - (competitor - cost) / cost x 100, to one
 * decimal, so the figure shown is the figure compared with the threshold.
 *
 * The cost is `resolveItemPricing(...).unitCost`, the unit cost Item Profit is
 * measured from - not re-derived here. Both prices are stored in USD on the
 * line; a ratio at one rate is currency-independent, so nothing is converted.
 *
 * null (shown "-") when either side is unusable: a missing/0 competitor price
 * or a missing/<=0 cost. NEVER 0 - that would read as "marked up by nothing".
 * Below cost is a real, negative answer.
 */
export const competitorMarkupPercent = (
  line: CompetitorLine | null | undefined
): number | null => {
  // A client_provided line's unitPrice is the CLIENT's price (we could not cost it): no USC cost to measure against.
  if (line?.pricingSource === 'client_provided') return null;
  const competitor = competitorPriceOf(line);
  const cost = resolveItemPricing(line).unitCost;
  const realized = realizedMarkupPercent(cost, competitor);
  if (realized === null) return null;
  return Math.round(realized * 10) / 10 + 0; // folds -0 into 0
};

/**
 * Is this markup under the Settings threshold? False for a missing markup and
 * for a threshold that has not loaded - the client never guesses a threshold.
 * Exactly AT the threshold is not below.
 */
export const isBelowCompetitorWarn = (
  pct: number | null | undefined,
  threshold: number | null | undefined
): boolean =>
  typeof pct === 'number' && Number.isFinite(pct) &&
  typeof threshold === 'number' && Number.isFinite(threshold) &&
  pct < threshold;
